import { createAnimationLegacy } from '../common/schemas/legacy/animation';
import { createExtendedPositionLegacy } from '../common/schemas/legacy/extended-position';
import { createWidgetPositionLegacy } from '../common/schemas/legacy/widget-position';

import type { LegacyText, LegacyTextDefault, LegacyTextZero, TextEntity } from './schemas';

/**
 * Host-only values the package deliberately doesn't own — the seeded rich-text
 * `content` is built from i18n strings in the host model, mirroring the button
 * widget's `buttonOverrides` convention.
 */
export type CreateLegacyTextOptions = {
    /** Seeded rich-text HTML (host builds it from `text_element_*` i18n keys). */
    content?: string;
};

/**
 * Field values shared by both placement variants — everything except
 * `position` (the only field the variants diverge on).
 */
const createCommonData = (options?: CreateLegacyTextOptions): Omit<LegacyTextDefault['data'], 'position'> => ({
    content: options?.content ?? '',
    horizontalAlign: 'L',
    isSynchronizeStyle: true,
    hideOnDesktop: false,
    hideOnTablet: false,
    hideOnMobile: false,
    animation: createAnimationLegacy(),
});

const createDefaultData = (options?: CreateLegacyTextOptions): LegacyTextDefault['data'] => ({
    ...createCommonData(options),
    position: createWidgetPositionLegacy(),
});

const createZeroData = (options?: CreateLegacyTextOptions): LegacyTextZero['data'] => ({
    ...createCommonData(options),
    // Zero blocks are laid out per-device — start with sync off (button precedent).
    isSynchronizeStyle: false,
    // Height is a placeholder: the text view DOM-syncs the rendered content
    // height into the grid (button's `setupZeroHeightSync` pattern).
    position: createExtendedPositionLegacy(),
});

export type LegacyTextVariant = 'default' | 'zero';

/**
 * Factory for the current legacy text entity — matches the persisted widget
 * JSON shape (`{ id, name: 'text', columnId, data, … }`) and the host model's
 * defaults. Variant selects between default-column placement (no `type` field,
 * simple position grid) and zero-block placement (`type: 'zero'`,
 * extended-position grid).
 */
export function createLegacyText(variant?: 'default', options?: CreateLegacyTextOptions): LegacyTextDefault;
export function createLegacyText(variant: 'zero', options?: CreateLegacyTextOptions): LegacyTextZero;
export function createLegacyText(
    variant: LegacyTextVariant = 'default',
    options?: CreateLegacyTextOptions
): LegacyText {
    if (variant === 'zero') {
        return {
            id: '',
            name: 'text',
            columnId: '',
            type: 'zero',
            data: createZeroData(options),
        };
    }

    return {
        id: '',
        name: 'text',
        columnId: '',
        data: createDefaultData(options),
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current default
 * for new text widgets. While only `legacy` exists, this delegates to
 * {@link createLegacyText}; pass the variant to opt into the zero-block tag.
 */
export function createText(variant?: 'default', options?: CreateLegacyTextOptions): LegacyTextDefault;
export function createText(variant: 'zero', options?: CreateLegacyTextOptions): LegacyTextZero;
export function createText(variant: LegacyTextVariant = 'default', options?: CreateLegacyTextOptions): TextEntity {
    return variant === 'zero' ? createLegacyText('zero', options) : createLegacyText('default', options);
}
